/**
 * Tooltip utility for managing tooltip interactions
 * Supports both hover and click triggers for desktop and mobile
 */
export class TooltipManager {
  constructor() {
    this.activeTooltips = new Set();
    this.isTouch = 'ontouchstart' in window;
    this.init();
  }

  /**
   * Initialize tooltip functionality
   */
  init() {
    this.setupTooltipListeners();
    this.setupDocumentListeners();
  }

  /**
   * Setup listeners for all tooltip triggers
   */
  setupTooltipListeners() {
    const tooltipTriggers = document.querySelectorAll('.tooltip-trigger');

    tooltipTriggers.forEach(trigger => {
      const tooltipId = trigger.getAttribute('data-tooltip-target');
      const tooltip = document.getElementById(tooltipId);

      if (!tooltip) {
        console.warn(`Tooltip with ID ${tooltipId} not found`);
        return;
      }

      // Desktop: hover events
      if (!this.isTouch) {
        trigger.addEventListener('mouseenter', () => {
          this.showTooltip(tooltip);
        });

        trigger.addEventListener('mouseleave', () => {
          this.hideTooltip(tooltip);
        });
      }

      // Both desktop and mobile: click events
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleTooltip(tooltip);
      });

      // Prevent tooltip from hiding when hovering over it
      tooltip.addEventListener('mouseenter', () => {
        if (!this.isTouch) {
          this.showTooltip(tooltip);
        }
      });

      tooltip.addEventListener('mouseleave', () => {
        if (!this.isTouch) {
          this.hideTooltip(tooltip);
        }
      });
    });
  }

  /**
   * Setup document-level listeners for closing tooltips
   */
  setupDocumentListeners() {
    // Close tooltips when clicking outside
    document.addEventListener('click', (e) => {
      const isTooltipTrigger = e.target.closest('.tooltip-trigger');
      const isTooltipContent = e.target.closest('.tooltip-content');

      if (!isTooltipTrigger && !isTooltipContent) {
        this.hideAllTooltips();
      }
    });

    // Close tooltips on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hideAllTooltips();
      }
    });
  }

  /**
   * Show tooltip with animation
   */
  showTooltip(tooltip) {
    if (this.activeTooltips.has(tooltip)) return;

    // Hide other tooltips first
    this.hideAllTooltips();

    tooltip.classList.remove('opacity-0', 'invisible', 'pointer-events-none');
    tooltip.classList.add('opacity-100', 'visible', 'pointer-events-auto');

    this.activeTooltips.add(tooltip);

    // Position tooltip correctly
    this.positionTooltip(tooltip);
  }

  /**
   * Hide tooltip with animation
   */
  hideTooltip(tooltip) {
    if (!this.activeTooltips.has(tooltip)) return;

    tooltip.classList.remove('opacity-100', 'visible', 'pointer-events-auto');
    tooltip.classList.add('opacity-0', 'invisible', 'pointer-events-none');

    this.activeTooltips.delete(tooltip);
  }

  /**
   * Toggle tooltip visibility
   */
  toggleTooltip(tooltip) {
    if (this.activeTooltips.has(tooltip)) {
      this.hideTooltip(tooltip);
    } else {
      this.showTooltip(tooltip);
    }
  }

  /**
   * Hide all active tooltips
   */
  hideAllTooltips() {
    this.activeTooltips.forEach(tooltip => {
      this.hideTooltip(tooltip);
    });
  }

  /**
   * Position tooltip to prevent overflow
   */
  positionTooltip(tooltip) {
    // Reset any previous positioning adjustments
    tooltip.classList.remove('left-0', 'right-0', 'top-full', 'mt-2');
    tooltip.classList.add('bottom-full', 'mb-2');
    
    // Ensure default centered positioning with inline styles
    tooltip.style.left = '50%';
    tooltip.style.transform = 'translateX(-50%)';

    // Get rect after ensuring proper positioning
    const rect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const margin = 10;

    // Only adjust horizontal position if there's actual overflow
    if (rect.left < margin) {
      // Too far left, align to left edge
      tooltip.style.left = '0';
      tooltip.style.transform = 'translateX(0)';
      console.log('Tooltip positioned to left edge due to overflow');
    } else if (rect.right > viewportWidth - margin) {
      // Too far right, align to right edge
      tooltip.style.left = 'auto';
      tooltip.style.right = '0';
      tooltip.style.transform = 'translateX(0)';
      console.log('Tooltip positioned to right edge due to overflow');
    } else {
      // Keep center position (default)
      tooltip.style.left = '50%';
      tooltip.style.right = 'auto';
      tooltip.style.transform = 'translateX(-50%)';
    }

    // Check vertical position
    if (rect.top < margin) {
      // Not enough space above, show below
      tooltip.classList.remove('bottom-full', 'mb-2');
      tooltip.classList.add('top-full', 'mt-2');
      console.log('Tooltip positioned below due to insufficient space above');
    }
  }

  /**
   * Get tooltip statistics
   */
  getStats() {
    return {
      activeTooltips: this.activeTooltips.size,
      isTouch: this.isTouch,
      totalTriggers: document.querySelectorAll('.tooltip-trigger').length
    };
  }

  /**
   * Destroy tooltip manager
   */
  destroy() {
    this.hideAllTooltips();
    this.activeTooltips.clear();
  }
}

// Export singleton instance
export const tooltipManager = new TooltipManager();
